/**
 * Feedback Service - API functions for user feedback on agent responses
 *
 * Provides methods for:
 * - Submitting feedback (thumbs up/down) on agent messages
 * - Getting feedback for a specific project
 * - Admin analytics (metrics and listing)
 */

import { jsonAuthHeaders, AUTH_FETCH_INIT } from '../auth/apiAuth';
import Logger from '../../utils/logger';
import type {
  FeedbackSubmission,
  FeedbackRecord,
  FeedbackMetrics,
  FeedbackListResponse,
  FeedbackSubmitResponse,
} from '../../types/feedback';

// Use VITE_API_BASE_URL directly - it already includes /api/v1
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '/api/v1';
const logger = Logger.create('FeedbackService');

class FeedbackService {
  private async getAuthHeaders(): Promise<HeadersInit> {
    // task_111: provider-aware. Firebase attaches a Bearer token; Studio omits
    // it (the gml_sso cookie carries auth, sent via credentials: 'include').
    return jsonAuthHeaders();
  }

  private async fetchWithAuth<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<T> {
    const headers = await this.getAuthHeaders();
    const response = await fetch(`${API_BASE_URL}${endpoint}`, {
      ...AUTH_FETCH_INIT,
      ...options,
      headers: {
        ...headers,
        ...options.headers,
      },
    });

    if (!response.ok) {
      let errorData;
      try {
        errorData = await response.json();
      } catch {
        errorData = { detail: response.statusText };
      }
      const errorMessage =
        errorData?.detail ||
        errorData?.error?.message ||
        errorData?.message ||
        'Unknown error';
      logger.error(`API error: ${response.status} - ${errorMessage}`);
      throw new Error(errorMessage);
    }

    return response.json();
  }

  /**
   * Submit feedback on an agent response.
   *
   * @param submission - The feedback data to submit
   * @returns Response with the created feedback ID
   */
  async submitFeedback(
    submission: FeedbackSubmission
  ): Promise<FeedbackSubmitResponse> {
    logger.info(
      `Submitting ${submission.rating} feedback for project: ${submission.project_id}, step: ${submission.step}`
    );

    return this.fetchWithAuth<FeedbackSubmitResponse>('/feedback', {
      method: 'POST',
      body: JSON.stringify(submission),
    });
  }

  /**
   * Get all feedback records for a specific project.
   *
   * @param projectId - The project ID
   * @returns Array of feedback records
   */
  async getProjectFeedback(projectId: string): Promise<FeedbackRecord[]> {
    logger.info(`Getting feedback for project: ${projectId}`);

    return this.fetchWithAuth<FeedbackRecord[]>(
      `/projects/${projectId}/feedback`
    );
  }

  /**
   * Get aggregated feedback metrics for admin dashboard.
   *
   * @param refresh - Whether to bypass cache and fetch fresh data
   * @returns Feedback metrics
   */
  async getFeedbackMetrics(refresh: boolean = false): Promise<FeedbackMetrics> {
    logger.info(`Getting feedback metrics (refresh: ${refresh})`);

    const params = refresh ? '?refresh=true' : '';
    return this.fetchWithAuth<FeedbackMetrics>(
      `/admin/feedback/metrics${params}`
    );
  }

  /**
   * List recent feedback records with optional filtering.
   *
   * @param options - Filtering and pagination options
   * @returns Paginated list of feedback items
   */
  async listFeedback(
    options: {
      limit?: number;
      offset?: number;
      step?: string;
      rating?: string;
    } = {}
  ): Promise<FeedbackListResponse> {
    const { limit = 50, offset = 0, step, rating } = options;
    logger.info(`Listing feedback (limit: ${limit}, offset: ${offset})`);

    const params = new URLSearchParams();
    params.set('limit', limit.toString());
    params.set('offset', offset.toString());
    if (step) params.set('step', step);
    if (rating) params.set('rating', rating);

    return this.fetchWithAuth<FeedbackListResponse>(
      `/admin/feedback/list?${params.toString()}`
    );
  }
}

// Export singleton instance
export const feedbackService = new FeedbackService();

// Export class for testing
export { FeedbackService };
